/**
 * Routes reachable without a session, in the two kinds that behave differently
 * when a session IS present.
 *
 * Shared by the edge proxy (which decides who gets redirected where) and by
 * client code that must not issue authenticated requests from these pages — a
 * 401 raised on a guest path has nowhere to send the user.
 *
 * Every route a person can arrive at with no session belongs here, not just the
 * sign-in screens. `/enroll-account` and `/verify-email` are opened from an
 * emailed link by someone who by definition has no session yet, and the survey
 * routes are emailed to respondents who may not have an account at all; leaving
 * them off sent all six to /login, which made enrolment and email verification
 * impossible to complete.
 *
 * Matching is by prefix (see `isGuestPath`), so the survey entries cover their
 * dynamic segments — `/survey-audience/{activityId}/{audienceId}/{response}`.
 */

/**
 * The sign-in screens. Someone who already has a session has no business here,
 * so the proxy sends them to the app instead.
 */
export const SIGN_IN_PATHS = [
  '/login',
  '/sso',
  '/forgot-password',
  // The shared host serves these under `/auth/` (rewritten in next.config); the
  // proxy runs before the rewrite, so it sees the `/auth/` spelling.
  '/auth/sso',
  '/auth/forgot-password',
] as const;

/**
 * Pages opened from an emailed link, each carrying its own token in the URL.
 *
 * These must render whether or not a session cookie is present, because the
 * TOKEN is what identifies the actor here — not the cookie. Bouncing a
 * cookie-holder to `/` strips the token out of the URL and there is no way back
 * to it but to find the email again, so the invitation, the verification or the
 * password reset simply cannot be completed.
 *
 * A cookie is far from unusual on these arrivals, and it need not even be a
 * live one: `hasSession` in the proxy is only "a refresh cookie exists", so a
 * long-expired session still counts. A new colleague opening their invitation on
 * a shared machine, or anyone who signed in here months ago, hit this. The
 * symptom reported was "the activation link redirects to the login page" —
 * bounced to `/`, whose authenticated queries then 401 and land on /login.
 *
 * v1 drew the same line and is the reference: `Public` (`routes.jsx:336-366`)
 * renders every public route regardless of `LOGIN_TOKEN`, redirecting an
 * authenticated visitor from `/logout` alone.
 */
export const ACTION_LINK_PATHS = [
  '/change-password',
  '/auth/change-password',
  // The SSO hand-off carries its own `?token=`, so a session holder following
  // it must not be bounced to `/` (that drops the token).
  '/sso-verification',
  '/auth/sso-verification',
  '/enroll-account',
  '/verify-email',
  '/adoption-survey',
  '/survey-activity-deliverer',
  '/survey-audience',
  '/survey-stakeholder',
] as const;

/**
 * Pages that must render with or without a session, and that no signed-in
 * visitor is turned away from. `/auth/handoff` is where a LEGACY sign-in lands:
 * that user has no cp_refresh by design. `/logout` is opened by legacy users too,
 * because the shared-host edge always routes it here, and must run its
 * localStorage clean-up rather than bounce to /login.
 */
export const SESSION_AGNOSTIC_PATHS = ['/auth/handoff', '/logout'] as const;

export const GUEST_PATHS = [
  ...SIGN_IN_PATHS,
  ...ACTION_LINK_PATHS,
  ...SESSION_AGNOSTIC_PATHS,
] as const;

function matchesAny(paths: readonly string[], pathname: string): boolean {
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * The SSO runtime's paths, each a single dash-suffixed segment with no slash
 * after the prefix, so none of them match the plain prefix rule below.
 *
 * All four are addresses an identity provider or an un-signed-in user arrives
 * at, and all four must render without a session — the assertion callback most
 * of all, since the provider POSTs to it precisely because the user has no
 * session yet. `/assert-` was the one that mattered and the one missing:
 * `/auth/assert-` was listed, but the address written into a tenant's IdP is
 * `${SSO_APP_URL}/assert-<companyId>`, with no `/auth`. `/metadata-` is fetched
 * by the provider itself, which never has a session.
 */
const SSO_RUNTIME_PREFIXES = ['/auth/assert-', '/assert-', '/sso-', '/ssologin-', '/metadata-'] as const;

export function isGuestPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  if (SSO_RUNTIME_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }
  return matchesAny(GUEST_PATHS, pathname);
}

/**
 * A sign-in screen, as opposed to an emailed action link. Only these turn a
 * signed-in visitor away.
 */
export function isSignInPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return matchesAny(SIGN_IN_PATHS, pathname);
}
